import {
  AutonomousVehicleRegistrationStatus,
  BusinessEntityService,
  BusinessType,
  CarrierType,
  MccsRoles,
  MotorCarriersService,
} from '@/@types/generated'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import Loading from '@/components/Atoms/Loading'
import { HasFeature, hasFeature, hasRole } from '@/components/Atoms/Permissions'
import { UserStampTooltip } from '@/components/Atoms/UserStampTooltip'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Header } from '@/components/ui/header'
import { Label } from '@/components/ui/label'
import { OutOfServiceLabel } from '@/components/OutOfServiceLabel'
import { StateOutOfServiceLabel } from '@/components/StateOutOfServiceLabel'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from '@/hooks/useAuth'
import { CompaniesFeature } from '@/lib/features/companies'
import { TicketsFeature } from '@/lib/features/tickets'
import format from '@/lib/format'
import { RevealButton } from '@/modules/licensing-agencies/components/RevealButton'
import { TicketOpenButton } from '@/modules/tickets/components/TicketOpenButton'
import { buildPath } from '@/routes'
import {
  Building2Icon,
  CheckIcon,
  PencilIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { Fragment, Suspense, useState } from 'react'
import { Outlet, useNavigate } from 'react-router'
import { useCompany } from '../hooks/company-provider'
import {
  BusinessEntityForm,
  TFormSchema,
} from './business-entity/BusinessEntityForm'
import CompanyDetailsNavigation from './CompanyDetailsNavigation'
import { Mcs150EmailReveal } from './Mcs150EmailReveal'
import { CertificatesFeature } from '@/lib/features'
import { useError } from '@/hooks/useError'

export function CompanyDetailsLayout() {
  // Hooks
  const navigate = useNavigate()
  const { user } = useAuth()
  const { company, reload } = useCompany()
  const { showError } = useError()

  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // What Company Information lets the user edit (the edit button itself needs CompaniesFeature.EditDetails)
  const avStatus = company.autonomousVehicleRegistration?.status
  // Nothing, while an AV registration is in progress
  const isAvRegistrationInProgress =
    avStatus === AutonomousVehicleRegistrationStatus.DRAFT ||
    avStatus === AutonomousVehicleRegistrationStatus.UNDER_REVIEW
  // Not the name, DBA or addresses of a USDOT company, which come from SAFER
  const hasUsdot = !!company.usdotNumber
  // CRD-026: not the Business Type of a motor carrier, which is changed in Business Structure
  const canEditBusinessType = !company.motorCarrier

  // "Motor Carrier, Broker" (when also registered as a broker) alongside the SAFER-sourced
  // Intrastate/Interstate operation, e.g. "Motor Carrier, Broker  |  Intra State".
  const carrierTypeLabel = company.motorCarrier
    ? ['Motor Carrier', company.motorCarrier.isAlsoBroker && 'Broker']
        .filter(Boolean)
        .join(', ')
    : ''
  const carrierType = [
    carrierTypeLabel,
    format.Enum(CarrierType, company.motorCarrier?.carrierType),
  ]
    .filter(Boolean)
    .join('  |  ')

  const canRevealMcs150Email = hasFeature(
    CertificatesFeature.ViewMcs150Email,
    user
  )

  const canViewBouncedEmails = hasRole(
    [
      MccsRoles.ADMINISTRATOR,
      MccsRoles.CREDENTIALING,
      MccsRoles.CREDENTIALING_ADMIN,
      MccsRoles.CHIEF_INVESTIGATOR,
    ],
    user
  )

  const _getMcs150Email = async () => {
    if (!canRevealMcs150Email) return ''

    try {
      setIsLoading(true)
      return MotorCarriersService.getMcs150Email(company.motorCarrier!.id!)
    } catch (e) {
      showError(
        'There was an issue retrieving the MCS-150 email.  Please try your request again'
      )
      console.error('There was an error retrieving MCS 150 email: ', e)
    } finally {
      setIsLoading(false)
    }

    return ''
  }

  const _toBouncedEmail = async () => {
    const email = await _getMcs150Email()
    navigate(
      `${buildPath('/bounced-emails')}?search=${encodeURIComponent(email)}`
    )
  }

  // Submit handler
  async function handleSubmit(data: TFormSchema) {
    try {
      await BusinessEntityService.update(company.id, data)
      setIsEditing(false)
      reload(true)
    } catch (err) {
      showError(err, 'Unable to save company information')
    }
  }

  return (
    <>
      <Breadcrumb
        label={format.CarrierName(company)}
        path={buildPath('/companies/:businessEntityId', {
          businessEntityId: company.id,
        })}
      />
      <div className="flex flex-col justify-between gap-4 md:flex-row">
        <div className="flex min-w-0 flex-col gap-1">
          <Header
            variant="h1"
            className="overflow-hidden overflow-ellipsis whitespace-nowrap"
          >
            {format.CarrierName(company)}
          </Header>
          {company.motorCarrier?.isOutOfService && <OutOfServiceLabel />}
          {company.motorCarrier?.isOutOfServiceState && (
            <StateOutOfServiceLabel />
          )}
        </div>
        <div className="flex flex-col items-start gap-1 lg:items-end">
          <div className="my-1 hidden grid-cols-[minmax(0,max-content)_1fr] gap-1 gap-x-2 text-right lg:grid">
            <Label className="justify-self-end text-sm whitespace-nowrap">
              Created
            </Label>
            <UserStampTooltip by={company.created} />

            <Label className="justify-self-end text-sm whitespace-nowrap">
              Updated
            </Label>
            <UserStampTooltip by={company.modified} />
          </div>
        </div>
      </div>
      {/* Mobile timestamps */}
      <div className="-mt-2 grid grid-cols-2 gap-4 md:flex lg:hidden">
        <div>
          <Label className="text-muted-foreground text-sm lg:text-right">
            Created
          </Label>
          <UserStampTooltip by={company.created} />
        </div>

        <div>
          <Label className="text-muted-foreground text-sm lg:text-right">
            Updated
          </Label>
          <UserStampTooltip by={company.modified} />
        </div>
      </div>

      <Card>
        <CardHeader className="flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-2xl font-semibold lg:col-span-2">
            <Building2Icon className="size-5 flex-none" />
            Company Information
            {/* Can the user even edit? */}
            <HasFeature feature={CompaniesFeature.EditDetails}>
              {!isEditing && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditing(true)}
                        aria-label="Edit company information"
                        disabled={isAvRegistrationInProgress}
                      >
                        <PencilIcon className="stroke-primary size-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {isAvRegistrationInProgress ? (
                    <TooltipContent>
                      Company information is not editable for in-progress or
                      under review autonomous vehicle registrations.
                    </TooltipContent>
                  ) : (
                    <TooltipContent side="right">Edit</TooltipContent>
                  )}
                </Tooltip>
              )}
            </HasFeature>
          </h3>

          <HasFeature feature={TicketsFeature.Create}>
            <TicketOpenButton businessEntityId={company.id} />
          </HasFeature>
        </CardHeader>
        {!isEditing && (
          <CardContent className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">
                Legal Business Name
              </Header>
              <Header variant="h5">{company.legalName}</Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">
                Doing Business As (DBA) Name
              </Header>
              <Header variant="h5">{company.dbaName || '\u2014'}</Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">USDOT</Header>
              <Header variant="h5" className="flex items-center gap-2">
                {company.usdotNumber || '\u2014'}
                {company.motorCarrier?.isOutOfService && (
                  <OutOfServiceLabel variant="inline" />
                )}
                {company.motorCarrier?.isOutOfServiceState && (
                  <StateOutOfServiceLabel variant="inline" />
                )}
              </Header>
            </div>

            {company.motorCarrier?.mcs150Email && (
              <div className="flex flex-col gap-2">
                <Header className="text-muted-foreground">MCS-150 Email</Header>
                <Header variant="h5" className="break-all">
                  {company.motorCarrier.mcs150EmailInvalid ? (
                    <Fragment>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-destructive flex items-center gap-1.5">
                            <TriangleAlertIcon className="size-4 flex-none" />
                            <RevealButton
                              canReveal={canRevealMcs150Email}
                              getSecret={_getMcs150Email}
                            >
                              {company.motorCarrier.mcs150Email || '\u2014'}
                            </RevealButton>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          This email was detected to bounce. Please check with
                          the carrier to update their email.
                        </TooltipContent>
                      </Tooltip>
                      {canViewBouncedEmails && (
                        <button
                          type="button"
                          onClick={_toBouncedEmail}
                          className="text-destructive cursor-pointer text-sm underline"
                        >
                          {isLoading ? <Loading /> : 'View in Bounced Emails'}
                        </button>
                      )}
                    </Fragment>
                  ) : (
                    <Mcs150EmailReveal businessEntity={company} />
                  )}
                </Header>
              </div>
            )}

            {company.autonomousVehicleContact?.email && (
              <div className="flex flex-col gap-2">
                <Header className="text-muted-foreground">
                  AV Primary Contact Email
                </Header>
                <Header variant="h5" className="break-all">
                  {company.autonomousVehicleContact.email}
                </Header>
              </div>
            )}

            {/* CRD-014: not role-gated, so insurance users see Business Type */}
            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Business Type</Header>
              <Header variant="h5">
                {format.Enum(BusinessType, company.businessType)}
              </Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Carrier Type</Header>
              <Header variant="h5">{carrierType}</Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Safety Rating</Header>
              <Header variant="h5">
                {company.motorCarrier?.safetyRating || '\u2014'}
              </Header>
            </div>

            {/* Only show if Texas Secretary of State File Number exists */}
            {company.stateFileNumber && (
              <div className="flex flex-col gap-2">
                <Header className="text-muted-foreground">
                  Texas Secretary of State File Number
                </Header>
                <Header variant="h5">{company.stateFileNumber}</Header>
              </div>
            )}

            <Separator className="my-2 sm:col-span-2 lg:col-span-3" />

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">
                Physical Address
              </Header>
              <Header variant="h5">
                {format.Address(company.physicalAddress)}
              </Header>
            </div>

            <div className="flex flex-col gap-2">
              <Header className="text-muted-foreground">Mailing Address</Header>
              <Header variant="h5">
                {format.Address(company.mailingAddress)}
              </Header>
            </div>
          </CardContent>
        )}
        {isEditing && (
          <CardContent className="grid grid-cols-1 gap-x-8 gap-y-2 sm:col-span-2 lg:col-span-3">
            <div className="flex flex-col gap-4">
              <BusinessEntityForm
                initialValues={{
                  legalName: company.legalName,
                  dbaName: company.dbaName,
                  businessType: company.businessType,
                  stateFileNumber: company.stateFileNumber || undefined,
                  physicalAddress: {
                    street1: company.physicalAddress.street1 || '',
                    street2: company.physicalAddress.street2 || '',
                    city: company.physicalAddress.city || '',
                    postalCode: company.physicalAddress.postalCode || '',
                    stateProvince: company.physicalAddress.stateProvince || '',
                  },
                  mailingAddress: {
                    street1: company.mailingAddress.street1 || '',
                    street2: company.mailingAddress.street2 || '',
                    city: company.mailingAddress.city || '',
                    postalCode: company.mailingAddress.postalCode || '',
                    stateProvince: company.mailingAddress.stateProvince || '',
                  },
                }}
                lockedByUsdot={hasUsdot}
                lockedDisplayValues={
                  hasUsdot
                    ? {
                        legalName: company.legalName,
                        dbaName: company.dbaName,
                        usdotNumber: company.usdotNumber?.toString(),
                        physicalAddress: format.Address(
                          company.physicalAddress
                        ),
                        mailingAddress: format.Address(company.mailingAddress),
                      }
                    : undefined
                }
                onSubmit={handleSubmit}
                withBusinessType={canEditBusinessType}
                withStateFileNumber
              >
                {({ isSubmitting }) => (
                  <div className="flex gap-2 lg:col-span-2">
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <Spinner /> : <CheckIcon />}
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </BusinessEntityForm>
            </div>
          </CardContent>
        )}
      </Card>

      <CompanyDetailsNavigation company={company} />

      <Suspense fallback={<Loading variant="fullscreen" />}>
        <Outlet />
      </Suspense>
    </>
  )
}
